import { gql } from '@apollo/client';

const GET_ASSESSMENTS = gql`
  query GetAssessments($year: Int!) {
    getAssessments(year: $year) {
      customerId
      year
      version
      c6Id
      c6Name
      customerName
      customerEmail
      channel
      omni
      marketGroup
      approvers
      reviewers
      performers
      status
      pendingUser
      score
      lastYearScore
      actions
      businessUnit
    }
  }
`;

const GET_ASSESSMENT_DATA = gql`
  query GetAssessmentData($customerId: String!, $year: Int!, $version: Int!) {
    getAnswers(customerId: $customerId, year: $year, version: $version) {
      questionId
      isValid
      isComplete
      selectedAnswer
      comment
      images
    }
    getSurvey(customerId: $customerId, year: $year, version: $version) {
      surveyId
      questionId
      headline
      subHeader
      questionType
      conditions
      explanation
      answerOptions {
        answerId
        text
        score
        subAnswerHeader
        minimumSubAnswers
        subAnswerOptions {
          answerId
          text
        }
      }
      answersExemptFromAttachments {
        answerId
      }
      furtherDetails
      commentsTextBox
      commentsInTextBox
      uploadHeadline
      upload
    }
    getSurveyInfo(customerId: $customerId, year: $year, version: $version) {
      active
      email
      contactEmails
      version
      uploadId
      assessmentStatus
      actions
      reviewers
      approvers
      performers
      assessmentDate
      approvalDate
      pendingUser
      comments {
        text
        date
        userEmail
      }
      surveyState {
        isComplete
        isEditable
        score
      }
      lastYearScore
      customerDetail {
        id
        name
        businessUnit
        c6Id
        c6Name
        channel
        omni
      }
    }
  }
`;

const APPROVE_ASSESSMENT = gql`
  mutation ApproveAssessment(
    $customerId: String!
    $year: Int!
    $version: Int!
    $comment: String
  ) {
    approveAssessment(
      customerId: $customerId
      year: $year
      version: $version
      comment: $comment
    ) {
      customerName
      customerEmail
    }
  }
`;

const REJECT_ASSESSMENT = gql`
  mutation RejectAssessment(
    $customerId: String!
    $year: Int!
    $version: Int!
    $comment: String
  ) {
    rejectAssessment(
      customerId: $customerId
      year: $year
      version: $version
      comment: $comment
    ) {
      customerName
      customerEmail
    }
  }
`;

const RESCORE_ASSESSMENT = gql`
  mutation RescoreAssessment(
    $customerId: String!
    $year: Int!
    $version: Int!
    $comment: String
  ) {
    rescoreAssessment(
      customerId: $customerId
      year: $year
      version: $version
      comment: $comment
    ) {
      customerName
      customerEmail
    }
  }
`;

const UPDATE_ANSWER = gql`
  mutation UpdateAnswer(
    $customerId: String!
    $answer: AnswerInput!
    $year: Int!
    $version: Int!
  ) {
    updateAnswer(
      customerId: $customerId
      answer: $answer
      year: $year
      version: $version
    ) {
      questionId
    }
  }
`;

const FINISH_SURVEY = gql`
  mutation FinishSurvey($customerId: String!, $year: Int!, $version: Int!) {
    finishSurvey(customerId: $customerId, year: $year, version: $version)
  }
`;

const UPDATE_ALL_ANSWERS = gql`
  mutation UpdateAllAnswers(
    $customerId: String!
    $answers: [AnswerInput!]!
    $year: Int!
    $version: Int!
  ) {
    updateAllAnswers(
      customerId: $customerId
      answers: $answers
      year: $year
      version: $version
    )
  }
`;

const DELETE_ASSESSMENT = gql`
  mutation DeleteAssessment($customerId: String!, $year: Int!, $version: Int!) {
    deleteAssessment(customerId: $customerId, year: $year, version: $version)
  }
`;

const SOFT_DELETE_ASSESSMENT = gql`
  mutation SoftDeleteAssessment(
    $customerId: String!
    $year: Int!
    $version: Int!
  ) {
    softDeleteAssessment(
      customerId: $customerId
      year: $year
      version: $version
    ) {
      customerId
      year
      version
    }
  }
`;

const GET_COMPLETE_PAGE_DATA = gql`
  query getCompletePageData($customerId: String!, $year: Int!, $version: Int!) {
    getSurvey(customerId: $customerId, year: $year, version: $version) {
      headline
      questionId
      questionType
      commentsTextBox
      upload
      answersExemptFromAttachments {
        answerId
      }
    }
    getAnswers(customerId: $customerId, year: $year, version: $version) {
      questionId
      isComplete
      selectedAnswer
      comment
      images
    }
    getSurveyInfo(customerId: $customerId, year: $year, version: $version) {
      uploadId
      assessmentStatus
      actions
      assessmentDate
      approvalDate
      pendingUser
      approvers
      performers
      reviewers
      surveyState {
        isComplete
        isEditable
        score
      }
      customerDetail {
        id
        name
        businessUnit
        c6Id
        c6Name
        channel
        omni
      }
    }
  }
`;

const ADD_ASSESSMENT_COMMENT = gql`
  mutation AddComment(
    $customerId: String!
    $year: Int!
    $version: Int!
    $comment: String!
  ) {
    addComment(
      customerId: $customerId
      year: $year
      version: $version
      comment: $comment
    ) {
      text
      date
      userEmail
    }
  }
`;

const UPDATE_ASSESSMENT_CHANNEL = gql`
  mutation UpdateAssessmentChannel($input: AssessmentChannelUpdateInput!) {
    updateAssessmentChannel(input: $input) {
      customerId
      year
      version
      channel
      status
      actions
    }
  }
`;

const GET_AVAILABLE_CHANNELS = gql`
  query GetAvailableChannels($year: Int!) {
    getAvailableChannelsForYear(year: $year)
  }
`;

const GET_USER_CONFIRMATION_STATUS = gql`
  query GetUserConfirmationStatus($customerId: String!) {
    getUserConfirmationStatus(customerId: $customerId) {
      email
      reminderSentDate
    }
  }
`;

const SEND_INVITATION_EMAIL = gql`
  mutation SendInvitationEmail(
    $customerId: String!
    $year: Int!
    $version: Int!
  ) {
    sendInvitationEmail(
      customerId: $customerId
      year: $year
      version: $version
    ) {
      wasEmailSent
      message
    }
  }
`;

export {
  GET_ASSESSMENTS,
  GET_ASSESSMENT_DATA,
  APPROVE_ASSESSMENT,
  REJECT_ASSESSMENT,
  RESCORE_ASSESSMENT,
  UPDATE_ANSWER,
  FINISH_SURVEY,
  UPDATE_ALL_ANSWERS,
  GET_COMPLETE_PAGE_DATA,
  DELETE_ASSESSMENT,
  ADD_ASSESSMENT_COMMENT,
  UPDATE_ASSESSMENT_CHANNEL,
  GET_AVAILABLE_CHANNELS,
  SOFT_DELETE_ASSESSMENT,
  GET_USER_CONFIRMATION_STATUS,
  SEND_INVITATION_EMAIL,
};
